import {Subscription} from 'rxjs';

import {globalWindow} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {AppStore} from '../../../src/store/appStore';
import {getHasSelectorPerformanceLogging} from '../../../src/store/featureFlags/featureFlagsSelectors';
import {
  disableSelectorInstrumentation,
  enableSelectorInstrumentation,
  SelectorComputationResult,
  selectorComputations$,
} from '../../helpers/reselectHelpers';
import {fromFocusEvent, isFocused} from '../../rx/focus';
import {fromVisibilityEvent, isHidden} from '../../rx/visibility';
import {logger} from '../logger/logger';
import {performance} from './performanceClient';
import {getHasPerformanceLogging, getHasPerformanceMetricLogging} from './performanceMetricSelectors';

/*
 * Constants.
 */

const selectorPerformanceLogger = logger.getLogger('selector-performance');
/** The number of ms a selector must take in order to be considered slow. */
const slowSelectorThreshold = 50;

/*
 * State.
 */

let subscription: Subscription | undefined;
let lastVisibilityChange = 0;
let lastFocusChange = 0;
let isPerformanceMetric: boolean = false;

/*
 * Api.
 */

export function setupSelectorPerformanceLogging(store: AppStore) {
  const handleStateUpdate = () => {
    const state = store.getState();
    if (getHasPerformanceLogging(state) && getHasSelectorPerformanceLogging(state)) {
      startSelectorPerformanceLogging();
    } else {
      stopSelectorPerformanceLogging();
    }
    isPerformanceMetric = getHasPerformanceMetricLogging(state);
  };

  store.subscribe(() => handleStateUpdate());
  handleStateUpdate();
}

function startSelectorPerformanceLogging() {
  if (subscription) {
    // Logging is already started.
    return;
  }

  enableSelectorInstrumentation();
  subscription = new Subscription();
  subscription.add(
    // Subscribe to visibility changes.
    fromVisibilityEvent(globalWindow).subscribe(() => {
      lastVisibilityChange = performance.now();
    }),
  );
  subscription.add(
    // Subscribe to focus changes. (This is a noop on mobile.)
    fromFocusEvent(globalWindow).subscribe(() => {
      lastFocusChange = performance.now();
    }),
  );
  subscription.add(selectorComputations$.subscribe(handleSelectorComputation));
}

export function stopSelectorPerformanceLogging() {
  if (!subscription) {
    // Logging is already stopped.
    return;
  }

  disableSelectorInstrumentation();
  subscription.unsubscribe();
  subscription = undefined;
}

/*
 * Helpers.
 */

function handleSelectorComputation(computation: SelectorComputationResult) {
  const {
    selectorName,
    duration,
    startTime,
    lastCombinerDuration,
    totalSelectorTime,
    didRecompute,
    recomputationCount,
    invocationCount,
  } = computation;

  if (duration < slowSelectorThreshold) {
    // Ignore fast selector computations.
    return;
  }

  if (isHidden(globalWindow) || startTime <= lastVisibilityChange) {
    // Ignore selector computations that occurred while the window was hidden.
    return;
  }

  if (!isFocused(globalWindow) || startTime <= lastFocusChange) {
    // Ignore selector computations that occurred while the window was unfocused.
    return;
  }

  selectorPerformanceLogger.warn('Slow selector', {
    selector_name: selectorName,
    duration,
    is_focused: isFocused(globalWindow),
    last_combiner_duration: lastCombinerDuration,
    did_recompute: didRecompute,
    recomputation_count: recomputationCount,
    invocation_count: invocationCount,
    total_selector_time: totalSelectorTime,
    is_performance_metric: isPerformanceMetric,
  });
}
